import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import { ShopProvider, useShop } from './context/ShopContext';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { ThemeProvider } from './context/ThemeContext';
import { AlertCircle, Loader2 } from 'lucide-react';

import ShopPage from './pages/ShopPage';
import ProductPage from './pages/ProductPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AccountPage from './pages/AccountPage';
import FavoritesPage from './pages/FavoritesPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import ContactPage from './pages/ContactPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import CookieConsent from './components/CookieConsent';
import { useViewportMobile } from './hooks/useViewportMobile';

import './index.css';

// Loading screen while shop data is being fetched
function ShopLoading() {
    return (
        <div className="min-h-screen bg-background flex items-center justify-center">
            <div className="text-center">
                <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
                <p className="text-muted-foreground">Loading shop...</p>
            </div>
        </div>
    );
}

// Error screen when shop cannot be loaded
function ShopError({ error }) {
    return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4">
            <div className="text-center max-w-md">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 text-red-600" />
                </div>
                <h1 className="font-display text-2xl font-semibold text-primary mb-2">
                    Shop Not Found
                </h1>
                <p className="text-muted-foreground mb-6">{error}</p>
                <div className="text-sm text-muted-foreground bg-muted/50 rounded-xl p-4">
                    <p className="font-medium mb-2">For Developers:</p>
                    <ul className="text-left list-disc list-inside space-y-1">
                        <li>Check REACT_APP_MERCHANT_ID in .env</li>
                        <li>Or use ?shop=subdomain for testing</li>
                        <li>Or deploy to subdomain.instagshop.com</li>
                    </ul>
                </div>
            </div>
        </div>
    );
}

// Main app content - rendered only when shop is loaded
function AppContent() {
    const { loading, error } = useShop();
    const isMobile = useViewportMobile();

    if (loading) {
        return <ShopLoading />;
    }

    if (error) {
        return <ShopError error={error} />;
    }

    return (
        <>
            <Routes>
                <Route path="/" element={<ShopPage />} />
                <Route path="/product/:id" element={<ProductPage />} />
                <Route path="/cart" element={<CartPage />} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/checkout/success" element={<CheckoutPage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/account" element={<AccountPage />} />
                <Route path="/favorites" element={<FavoritesPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <CookieConsent />
            <Toaster
                position={isMobile ? 'top-center' : 'top-right'}
                richColors
                closeButton
                toastOptions={{
                    style: {
                        borderRadius: '14px',
                    },
                }}
            />
        </>
    );
}

function App() {
    return (
        <HelmetProvider>
            <BrowserRouter>
                <ShopProvider>
                    <ThemeProvider>
                        <AuthProvider>
                            <CartProvider>
                                <AppContent />
                            </CartProvider>
                        </AuthProvider>
                    </ThemeProvider>
                </ShopProvider>
            </BrowserRouter>
        </HelmetProvider>
    );
}

export default App;
